import { supabase } from '../supabaseClient';
import { IMAGE_WEBP_EXT, IMAGE_WEBP_MIME } from '../utils/imageCompress';
import {
  fetchSlideImageStorageUsage,
  notifySlideImageStorageChanged,
} from './slideImageStorageService';

const BUCKET = 'member-profile-images';
const CACHE_CONTROL = '86400';

function memberPhotoPath(
  userId: string,
  workspaceId: string,
  gradeId: string,
  memberId: string
): string {
  return `${userId}/${workspaceId}/${gradeId}/MemberProfile/${memberId}.${IMAGE_WEBP_EXT}`;
}

export async function uploadMemberPhoto(params: {
  workspaceId: string;
  gradeId: string;
  memberId: string;
  blob: Blob;
}): Promise<{ path: string }> {
  const { workspaceId, gradeId, memberId, blob } = params;
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (userErr || !userId) throw new Error('로그인이 필요합니다.');

  const usage = await fetchSlideImageStorageUsage();
  if (usage && usage.bytesUsed + blob.size > usage.bytesLimit) {
    throw new Error('QUOTA_EXCEEDED');
  }

  const path = memberPhotoPath(userId, workspaceId, gradeId, memberId);
  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(path, blob, {
    contentType: IMAGE_WEBP_MIME,
    cacheControl: CACHE_CONTROL,
    upsert: true,
  });
  if (uploadErr) throw uploadErr;

  try {
    const { error: regErr } = await supabase.rpc('register_member_profile_image_asset', {
      p_path: path,
      p_bytes: blob.size,
      p_workspace_id: workspaceId || null,
      p_grade_id: gradeId || null,
      p_member_id: memberId || null,
    });
    if (regErr) {
      if (String(regErr.message ?? '').includes('QUOTA_EXCEEDED')) {
        throw new Error('QUOTA_EXCEEDED');
      }
      throw regErr;
    }
  } catch (e) {
    // 등록 실패 시 방금 올린 파일 롤백(이전 파일이 있었다면 upsert로 덮인 상태일 수 있음)
    await supabase.storage.from(BUCKET).remove([path]).catch(() => undefined);
    throw e;
  }

  notifySlideImageStorageChanged();
  return { path };
}

export async function deleteMemberPhoto(params: {
  workspaceId: string;
  gradeId: string;
  memberId: string;
  storagePath?: string | null;
}): Promise<void> {
  const { workspaceId, gradeId, memberId, storagePath } = params;
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (!userId) return;

  const path =
    (storagePath ?? '').trim() || memberPhotoPath(userId, workspaceId, gradeId, memberId);

  try {
    await supabase.rpc('release_member_profile_image_asset', { p_path: path });
  } catch {
    /* 메타 없으면 무시 */
  }

  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error && !/not.?found|Object not found/i.test(error.message ?? '')) {
    throw error;
  }
  notifySlideImageStorageChanged();
}

/**
 * DB의 photo_profile_path 가 비어 있어도
 * 1) member_profile_image_assets 메타 또는
 * 2) Storage MemberProfile 폴더
 * 에 파일이 남아 있으면 멤버 id ↔ path로 매칭한다.
 * (과거 일괄 저장이 path만 지운 고아 파일 복구용)
 */
export async function findExistingMemberPhotoPaths(params: {
  workspaceId: string;
  gradeId: string;
  memberIds: string[];
}): Promise<Map<string, string>> {
  const { workspaceId, gradeId, memberIds } = params;
  const out = new Map<string, string>();
  const ids = Array.from(new Set(memberIds.map((id) => id.trim()).filter(Boolean)));
  if (!workspaceId || !gradeId || ids.length === 0) return out;

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (!userId) return out;

  const idSet = new Set(ids);
  const suffix = `.${IMAGE_WEBP_EXT}`;
  const folder = `${userId}/${workspaceId}/${gradeId}/MemberProfile`;

  // 1) 할당량 메타(업로드 성공 시 남음) — Storage list보다 안정적
  const { data: assetRows } = await supabase
    .from('member_profile_image_assets')
    .select('member_id, storage_path, grade_id, workspace_id')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);

  for (const row of (assetRows ?? []) as Array<{
    member_id?: string | null;
    storage_path?: string | null;
  }>) {
    const memberId = (row.member_id ?? '').trim();
    const path = (row.storage_path ?? '').trim();
    if (!memberId || !path || !idSet.has(memberId)) continue;
    if (!path.includes('/MemberProfile/')) continue;
    out.set(memberId, path);
  }

  // 2) Storage 폴더 직접 목록 (메타가 없는데 파일만 남은 경우)
  const { data: listed } = await supabase.storage.from(BUCKET).list(folder, {
    limit: 200,
    offset: 0,
  });
  for (const entry of listed ?? []) {
    const name = (entry.name ?? '').trim();
    if (!name.endsWith(suffix)) continue;
    const memberId = name.slice(0, -suffix.length);
    if (!idSet.has(memberId) || out.has(memberId)) continue;
    out.set(memberId, `${folder}/${name}`);
  }

  return out;
}

/** 현재 학년에서 class_members path와 끊긴 MemberProfile 자산 개수(고아 추정) */
export async function countOrphanMemberProfileAssets(params: {
  workspaceId: string;
  gradeId: string;
}): Promise<{ assetCount: number; linkedCount: number; orphanCount: number }> {
  const { workspaceId, gradeId } = params;
  if (!workspaceId || !gradeId) {
    return { assetCount: 0, linkedCount: 0, orphanCount: 0 };
  }

  const [{ data: assets }, { data: members }] = await Promise.all([
    supabase
      .from('member_profile_image_assets')
      .select('storage_path, member_id')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId),
    supabase
      .from('class_members')
      .select('id, photo_profile_path')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId),
  ]);

  const memberProfileAssets = (
    (assets ?? []) as Array<{ storage_path?: string | null; member_id?: string | null }>
  ).filter((a) => (a.storage_path ?? '').includes('/MemberProfile/'));

  const linkedPaths = new Set(
    ((members ?? []) as Array<{ photo_profile_path?: string | null }>)
      .map((m) => (m.photo_profile_path ?? '').trim())
      .filter(Boolean)
  );

  const assetCount = memberProfileAssets.length;
  let linkedCount = 0;
  for (const a of memberProfileAssets) {
    const path = (a.storage_path ?? '').trim();
    if (path && linkedPaths.has(path)) linkedCount += 1;
  }

  return {
    assetCount,
    linkedCount,
    orphanCount: Math.max(0, assetCount - linkedCount),
  };
}

